import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Popper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import Iconify from "../../../minimals/components/iconify";
import { logEvent } from "../../utils/analyticsLogger";
import { OtherContactImg } from "../../orderEntry/additionalContacts/ContactCardNew";
import { formatNamesForPatriotAndBlacklistSearch } from "../../orderEntry/utils/blacklistSearch";
import AddCompanyPage from "../../phonebook/company/AddCompanyPage";
import AddEditCompany from "../../phonebook/company/addEditCompany/AddEditCompany";
import { AddEditCompanyProvider } from "../../phonebook/company/addEditCompany/AddEditCompanyContext";
import AddEditContactForm from "../../phonebook/contact/addEditContact/AddEditContactForm";
import { AddEditContactProvider } from "../../phonebook/contact/addEditContact/AddEditContactContext";
import {
  DocumentsBlacklistSearchResults,
  DocumentsBlackListResult,
  DocumentsAccountRep,
  DocumentsCompanyBranch,
  DocumentsContactType,
  DocumentsOrderContact,
  DocumentsOrderInfo,
  DocumentsRep,
  DocumentsTitleCompany,
  DocumentsTransactingParty,
  DocumentsTransactingPartyAddress,
  DocumentsTransactingPartyIndividual,
  DocumentsTransactingPartyJoint,
  DocumentsTransactingPartyOrganization,
  DocumentsUnderwriter,
} from "../types";

const buyerSellerContactTypes = ["Buyer", "Buyer/Borrower", "Seller"];

const MomentumRoot = styled.div<{ iframeDisplay?: boolean }>`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.22);
  box-sizing: border-box;
  display: flex;
  height: 100vh;
  justify-content: center;
  left: 0;
  padding: 40px;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 49944;

  .MuiOutlinedInput-root {
    border-radius: 8px;
  }

  .tile {
    background-color: #fff;
    border-radius: 10px;
    box-sizing: border-box;
    max-height: calc(100vh - 48px);
    max-width: 720px;
    overflow: auto;
    padding: 32px;
    position: absolute;
    right: ${(props) => (props.iframeDisplay ? "756px" : "auto")};
    transition: all 0.3s ease;
    width: 100%;
  }

  .tile_header_wrap {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  .tile_header {
    font-size: 28px;
    font-weight: 600;
    line-height: 1.2;
  }

  .df {
    display: flex;
  }

  .jcc {
    justify-content: center;
  }

  .jcsb {
    justify-content: space-between;
  }

  .aic {
    align-items: center;
  }

  .f-wrap {
    flex-wrap: wrap;
  }

  .full-width {
    width: 100%;
  }

  .no-contact-yet {
    align-items: flex-start;
    display: flex;
    gap: 40px;
    margin-top: 24px;
    width: 100%;
  }

  .mt5 {
    margin-top: 24px;
  }

  .gap1 {
    gap: 8px;
  }

  .gap2 {
    gap: 14px;
  }

  @media (max-width: 760px) {
    padding: 16px;

    .tile {
      max-height: calc(100vh - 24px);
      padding: 20px;
      width: calc(100vw - 24px);
    }

    .tile_header {
      font-size: 22px;
    }

    .no-contact-yet {
      flex-direction: column;
      gap: 24px;
    }
  }
`;

const ErrorMessageWrap = styled.div`
  background-color: #f7f7f9;
  border: 2px solid #ee765c;
  border-radius: 10px;
  margin-bottom: 20px;
  padding: 20px;

  h2 {
    color: #ee765c;
    font-size: 18px;
    margin: 0;
  }
`;

const PhonebookWarningWrap = styled.div<{ show?: boolean }>`
  bottom: 0;
  left: 32px;
  overflow: ${(props) => (props.show ? "visible" : "hidden")};
  position: absolute;
  transition: all 0.1s ease-in-out;
  width: calc(100% - 64px);

  .warning-inner {
    background-color: #f2f2f2;
    border-radius: 10px;
    bottom: ${(props) => (props.show ? "24px" : "-320px")};
    display: flex;
    flex-direction: column;
    font-size: 15px;
    gap: 24px;
    justify-content: space-between;
    line-height: 1.5;
    padding: 24px;
    position: absolute;
    transition: all 0.3s ease;
    width: 100%;
    z-index: 550;
  }

  .warning-title {
    color: #f80202;
    font-size: 18px;
    font-weight: 600;
  }

  .ok-button {
    align-self: flex-end;
    background-color: #141368;
    color: #fff;
  }
`;

const FormWrapStyles = styled.div`
  border-radius: 16px;
  max-width: calc(100% - 280px);
  padding: 0 0 16px;
  width: 100%;

  @media (max-width: 760px) {
    max-width: 100%;
  }
`;

const PhonebookAutocompletePopper = (
  props: React.ComponentProps<typeof Popper>,
) => (
  <Popper
    {...props}
    placement="bottom-end"
    style={{
      ...props.style,
      maxWidth: "calc(100vw - 48px)",
      width: 600,
    }}
  />
);

const ContactPreviewWrap = styled.div`
  background-color: #fff;
  border-radius: 16px;
  box-shadow:
    0 0 2px rgba(145, 158, 171, 0.2),
    0 12px 24px -4px rgba(145, 158, 171, 0.12);
  max-width: 240px;
  overflow: hidden;
  padding-bottom: 24px;
  position: relative;
  width: 100%;

  .card-top {
    align-items: center;
    background: #141368;
    color: #fff;
    display: flex;
    height: 148px;
    justify-content: center;
    position: relative;

    &::after {
      background: #fff;
      border-radius: 50% 50% 0 0 / 70% 70% 0 0;
      bottom: -1px;
      content: "";
      height: 54px;
      left: 0;
      position: absolute;
      width: 100%;
    }
  }

  .card-avatar {
    align-items: center;
    background: #fff;
    border: 6px solid currentColor;
    border-radius: 999px;
    bottom: -38px;
    color: #141368;
    display: flex;
    height: 78px;
    justify-content: center;
    position: absolute;
    width: 78px;
    z-index: 1;
  }

  .card-avatar.placeholder {
    color: #8d90bf;
  }

  .placeholder-avatar {
    background: transparent;
  }

  .edit-icon {
    color: #fff;
    cursor: pointer;
    position: absolute;
    right: 12px;
    top: 12px;
    z-index: 1;
  }

  .contact-name {
    font-size: 16px;
    font-weight: 700;
    margin: 56px 20px 4px;
    text-align: center;
    word-break: break-word;
  }

  .contact-type {
    color: #637381;
    font-size: 12px;
    margin: 0 20px;
    text-align: center;
  }

  .contact-info {
    padding: 0 20px;
    text-align: left;
  }

  .contact-info > div {
    align-items: center;
    display: flex;
    font-size: 12px;
    gap: 8px;
    margin-top: 16px;
    word-break: break-word;
  }
`;

const AdditionalEmailsWrap = styled.div`
  background-color: rgb(248, 249, 250);
  border-radius: 12px;
  margin-top: 24px;
  padding: 20px;

  .heading {
    align-items: center;
    display: flex;
    font-size: 18px;
    font-weight: 500;
    justify-content: center;
    margin-bottom: 20px;
  }

  .email-row {
    align-items: center;
    display: flex;
    gap: 8px;
    margin-top: 18px;
    max-width: 355px;
    width: 100%;
  }

  .add-button {
    align-items: center;
    color: #4440db;
    cursor: pointer;
    display: inline-flex;
    font-size: 14px;
    gap: 4px;
    margin-top: 24px;
  }
`;

const ButtonsWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const SuccessWrap = styled.div`
  align-items: center;
  background-color: #e9fcd4;
  border-radius: 8px;
  color: #08660d;
  display: flex;
  font-weight: 600;
  gap: 14px;
  justify-content: space-between;
  margin-top: 24px;
  min-height: 50px;
  padding: 0 16px;

  button {
    background-color: #54d62c;
    border: 0;
    border-radius: 4px;
    color: #fff;
    cursor: pointer;
    height: 32px;
    padding: 0 12px;
  }
`;

const DeleteConfirmWrap = styled.div<{ show?: boolean }>`
  bottom: 0;
  left: 32px;
  overflow: ${(props) => (props.show ? "visible" : "hidden")};
  position: absolute;
  transition: all 0.1s ease-in-out;
  width: calc(100% - 64px);

  .comingSoonInner {
    background-color: #f2f2f2;
    border-radius: 10px;
    bottom: ${(props) => (props.show ? "24px" : "-320px")};
    display: flex;
    flex-direction: column;
    font-size: 15px;
    justify-content: space-between;
    left: 0;
    line-height: 1.5;
    padding: 24px;
    position: absolute;
    transition: all 0.3s ease;
    width: 100%;
    z-index: 550;
  }

  .coming-soon {
    color: #141368;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 24px;
  }
`;

const ToggleButtonWrap = styled.div`
  background-color: rgb(255, 255, 255);
  border: 1px solid rgba(145, 158, 171, 0.16);
  border-radius: 8px;
  display: inline-flex;
  gap: 4px;
  padding: 4px;

  button {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 8px;
    color: rgb(99, 115, 129);
    cursor: pointer;
    display: inline-flex;
    font-size: 0.8125rem;
    font-weight: 700;
    height: 38px;
    justify-content: center;
    padding: 0 10px;
  }

  button.active {
    background-color: rgba(24, 23, 168, 0.08);
    color: rgb(24, 23, 168);
    cursor: default;
  }
`;

const vestingOptions = [
  ",a corporation",
  ",a Georgia limited liability company",
  ",a limited liability company",
  ",a Limited Liability Partnership",
  ",a partnership",
  ",as community property",
  ",as her sole and separate property",
  ",as his sole and separate property",
  ",as joint tenants",
  ",as sole and separate property",
  ",as tenants by the entirety",
  ",as tenants in common",
  "Purchaser with contractual rights under a purchase agreement with the vested owner identified at Item 4 below.",
  "Trust",
];

const organizationTypeOptions = [
  "Corporation",
  "Limited Liability Corp.",
  "Limited Liability Company",
  "Limited Partnership",
  "Partnership",
  "Trust",
  "Estate",
  "Other",
];

const jointRelationshipOptions = [
  ", a married man",
  ", a married person",
  ", a married woman",
  ", a single man",
  ", a single person",
  ", a single woman",
  ", a widow",
  ", a widower",
  ", an unmarried man",
  ", an unmarried person",
  ", an unmarried woman",
  ", as Executrix",
  ", her husband",
  ", his wife",
  "husband and wife",
  "wife and husband",
  "and",
  "and husband,",
  "and wife",
  "and wife,",
  "his wife",
  "husband,",
  ", as Trustee",
  ", joint tenants with right of survivorship",
  ", unmarried",
  ", tenants by the entirety",
  ", single",
  ", married",
  ", as Administrator",
];

const defaultAddress = (): DocumentsTransactingPartyAddress => ({
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
});

const defaultIndividual = (
  gender = "Male",
): DocumentsTransactingPartyIndividual => ({
  firstName: "",
  middleName: "",
  lastName: "",
  gender,
});

const defaultOrganization = (): DocumentsTransactingPartyOrganization => ({
  name: "",
  organizationType: "",
  payeeName: "",
  vesting: "",
  address: defaultAddress(),
  isPayeeNameSameAsName: true,
});

const defaultJoint = (): DocumentsTransactingPartyJoint => ({
  relationshipType: "",
  jointOne: defaultIndividual("Male"),
  jointTwo: defaultIndividual("Female"),
});

const emptyContact = (): DocumentsOrderContact => ({
  id: "",
  isMarketingSource: false,
  emailsSplit: [],
});

const getContactName = (contact?: DocumentsOrderContact | null) =>
  contact?.name || contact?.payeeName || contact?.company || "";

const getRepLabel = (rep: DocumentsRep) =>
  rep.fullName || rep.displayName || rep.name || "";

const isNumericIdentifier = (value?: string | number | null) =>
  typeof value === "number" ||
  (typeof value === "string" && /^\d+$/.test(value));

const isTitleUnderwriterType = (typeName?: string | null) =>
  ["titlecompany", "underwriter"].includes(normalizedText(typeName));

const isOrderTitleCompanyType = (typeName?: string | null) =>
  normalizedText(typeName) === "titlecompany";

const isAccountRepType = (typeName?: string | null) => {
  const normalized = normalizedText(typeName);
  return (
    normalized.includes("accountrep") ||
    normalized.includes("acctrep") ||
    (normalized.includes("account") &&
      (normalized.includes("rep") || normalized.includes("representative")))
  );
};

const normalizedText = (value?: string | null) =>
  (value || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

const isPrincipalContactType = (typeName?: string | null) =>
  normalizedText(typeName).includes("principal");

const isInternalUserContactType = (typeName?: string | null) => {
  const normalized = normalizedText(typeName);
  return (
    normalized.includes("salesperson") ||
    normalized.includes("salesrep") ||
    isAccountRepType(typeName) ||
    normalized === "funder"
  );
};

const looseTextMatch = (left?: string | null, right?: string | null) => {
  const normalizedLeft = normalizedText(left);
  const normalizedRight = normalizedText(right);
  return Boolean(
    normalizedLeft &&
    normalizedRight &&
    (normalizedLeft === normalizedRight ||
      normalizedLeft.includes(normalizedRight) ||
      normalizedRight.includes(normalizedLeft)),
  );
};

const contactMatchesTitleCompany = (
  contact: DocumentsOrderContact,
  option: DocumentsTitleCompany,
) =>
  Boolean(
    (contact.lookupCode && option.lookupCode === contact.lookupCode) ||
    looseTextMatch(contact.name, option.name) ||
    looseTextMatch(contact.payeeName, option.name) ||
    looseTextMatch(contact.company, option.name),
  );

const contactMatchesUnderwriter = (
  contact: DocumentsOrderContact,
  option: DocumentsUnderwriter,
) =>
  Boolean(
    (contact.lookupCode &&
      (option.lookupCode === contact.lookupCode ||
        option.underwriterLookupCode === contact.lookupCode ||
        option.underwriterLookupCode?.endsWith(`-${contact.lookupCode}`))) ||
    looseTextMatch(contact.name, option.payeeName) ||
    looseTextMatch(contact.payeeName, option.payeeName) ||
    looseTextMatch(contact.company, option.payeeName),
  );

const getUnderwriterLookupCode = (underwriter?: DocumentsUnderwriter | null) =>
  underwriter?.underwriterLookupCode ||
  (underwriter as { underWriterLookupCode?: string } | null)
    ?.underWriterLookupCode ||
  "";

const getBranchFromContact = (
  contact: DocumentsOrderContact,
): DocumentsCompanyBranch | null => {
  if (
    !contact.address1 &&
    !contact.address2 &&
    !contact.city &&
    !contact.state &&
    !contact.zip
  ) {
    return null;
  }

  return {
    address1: contact.address1 || "",
    address2: contact.address2 || "",
    city: contact.city || "",
    state: contact.state || "",
    zip: contact.zip || "",
  };
};

const repMatches = (
  rep: DocumentsRep,
  contact?: DocumentsOrderContact | DocumentsAccountRep | null,
) => {
  if (!contact) {
    return false;
  }

  const repLabel = normalizedText(getRepLabel(rep));
  return [
    (contact as DocumentsRep).id?.toString(),
    contact.name,
    (contact as DocumentsAccountRep).fullName,
    (contact as DocumentsAccountRep).email,
  ]
    .map((value) => normalizedText(value))
    .filter(Boolean)
    .some(
      (value) =>
        value === repLabel || value === normalizedText(rep.id?.toString()),
    );
};

const uniqueRepsByName = (reps: DocumentsRep[]) => {
  const repsByName: Record<string, DocumentsRep> = {};
  reps.forEach((rep) => {
    const name = getRepLabel(rep);
    if (name && !repsByName[name]) {
      repsByName[name] = rep;
    }
  });
  return Object.values(repsByName);
};

const normalizeGovernmentContact = (
  contact: DocumentsOrderContact & Record<string, any>,
): DocumentsOrderContact => ({
  ...contact,
  address1: contact.address1 || contact.addressLine1 || "",
  address2: contact.address2 || contact.addressLine2 || "",
  contactType: "Government",
  email: contact.email || "",
  emailsSplit: [],
  fax: contact.fax || "",
  isMarketingSource: false,
  lookupCode: contact.lookupCode || "",
  name: contact.name || "",
  payeeName: contact.payeeName || "",
  phone: contact.phone || "",
  type: "Government",
});

const validEmail = (email?: string) =>
  !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getFullName = ({
  name,
  firstName,
  middleName,
  lastName,
}: {
  name?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}) => {
  if (name) {
    return name;
  }

  if (!firstName || firstName === "TBD" || !lastName) {
    return "";
  }

  return [firstName, middleName, lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ");
};

const getProxyContactTypes = async () => {
  const { data } = await axios.get<DocumentsContactType[]>(
    "/proxy/api/contacts/availablecontacttypes?includeignored=true",
  );
  const momentumExtras: DocumentsContactType[] = [
    {
      name: "Sales Person",
      isOther: false,
      hasLookupApi: false,
      isPhonebookContact: true,
    },
    {
      name: "Account Rep",
      isOther: false,
      hasLookupApi: false,
      isPhonebookContact: true,
    },
    {
      name: "Buyers Attorney",
      isOther: false,
      hasLookupApi: false,
      isPhonebookContact: true,
    },
    {
      name: "Sellers Attorney",
      isOther: false,
      hasLookupApi: false,
      isPhonebookContact: true,
    },
    {
      name: "Lenders Attorney",
      isOther: false,
      hasLookupApi: false,
      isPhonebookContact: true,
    },
  ];

  const sortedData = [...(data ?? []), ...momentumExtras].sort(
    (left, right) => {
      if (left.name === right.name) {
        return left.isOther === right.isOther ? 0 : left.isOther ? 1 : -1;
      }

      return (left.name || "").localeCompare(right.name || "");
    },
  );

  return sortedData.reduce<DocumentsContactType[]>((uniqueTypes, type) => {
    if (!uniqueTypes.some((existing) => existing.name === type.name)) {
      uniqueTypes.push(type);
    }
    return uniqueTypes;
  }, []);
};

const getContactTypeLabel = (option: DocumentsContactType | string) => {
  if (typeof option === "string") {
    return option;
  }

  if (option.isOther && !option.name) {
    return "Other";
  }

  return option.name || "";
};

const getFallbackContactType = (
  typeName: string,
  contact?: DocumentsOrderContact | null,
): DocumentsContactType => {
  const fallbackIsTitleUnderwriter = isTitleUnderwriterType(typeName);
  const fallbackIsOther =
    normalizedText(typeName) === "other" || Boolean(contact?.otherContactType);

  return {
    name: typeName,
    isOther: fallbackIsOther,
    hasLookupApi: Boolean(contact?.lookupCode) && !fallbackIsTitleUnderwriter,
    isPhonebookContact:
      Boolean(contact?.lookupCode) && !fallbackIsTitleUnderwriter,
  };
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  orderNumber: string;
  orderInfo?: DocumentsOrderInfo;
  contacts: DocumentsOrderContact[];
  contact?: DocumentsOrderContact | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

type AddressSuggestion = {
  entries?: number;
  aptNo?: string;
  address?: string;
  formattedAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

type NativePhonebookDialogState =
  | {
      pageView: "create-contact";
      defaultName: string;
    }
  | {
      pageView: "create-company";
      defaultName: string;
      defaultCompanyType: string;
    }
  | {
      pageView: "edit-contact";
      id: number | string;
    }
  | {
      pageView: "edit-company";
      id: number | string;
    };

export function OrderContactEditorDialog({
  open,
  mode,
  orderNumber,
  orderInfo,
  contacts,
  contact: editContact,
  onClose,
  onSaved,
}: Props) {
  const [contactTypes, setContactTypes] = useState<DocumentsContactType[]>([]);
  const [contactType, setContactType] = useState<DocumentsContactType | null>(
    null,
  );
  const [draft, setDraft] = useState<DocumentsOrderContact>(emptyContact());
  const [selectedBranch, setSelectedBranch] =
    useState<DocumentsCompanyBranch | null>(null);
  const [branchOptions, setBranchOptions] = useState<DocumentsCompanyBranch[]>(
    [],
  );
  const [phonebookOptions, setPhonebookOptions] = useState<
    DocumentsOrderContact[]
  >([]);
  const [phonebookSearchText, setPhonebookSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invalidEmailIndexes, setInvalidEmailIndexes] = useState<number[]>([]);
  const [salesPersonOptions, setSalesPersonOptions] = useState<DocumentsRep[]>(
    [],
  );
  const [salesReps, setSalesReps] = useState<(DocumentsRep | null)[]>([
    null,
    null,
    null,
  ]);
  const [accountRepOptions, setAccountRepOptions] = useState<DocumentsRep[]>(
    [],
  );
  const [selectedAccountRep, setSelectedAccountRep] =
    useState<DocumentsRep | null>(null);
  const [titleCompanyOptions, setTitleCompanyOptions] = useState<
    DocumentsTitleCompany[]
  >([]);
  const [underwriterOptions, setUnderwriterOptions] = useState<
    DocumentsUnderwriter[]
  >([]);
  const [selectedTitleCompany, setSelectedTitleCompany] =
    useState<DocumentsTitleCompany | null>(null);
  const [selectedUnderwriter, setSelectedUnderwriter] =
    useState<DocumentsUnderwriter | null>(null);
  const [sameAsPropertyAddress, setSameAsPropertyAddress] = useState(true);
  const [isOrganization, setIsOrganization] = useState(false);
  const [isJoint, setIsJoint] = useState(false);
  const [individual, setIndividual] =
    useState<DocumentsTransactingPartyIndividual>(defaultIndividual());
  const [organization, setOrganization] =
    useState<DocumentsTransactingPartyOrganization>(defaultOrganization());
  const [joint, setJoint] =
    useState<DocumentsTransactingPartyJoint>(defaultJoint());
  const [address, setAddress] =
    useState<DocumentsTransactingPartyAddress>(defaultAddress());
  const [searchResults, setSearchResults] =
    useState<DocumentsBlacklistSearchResults | null>(null);
  const [dealOwners, setDealOwners] = useState<string[]>([]);
  const [isDealOwner, setIsDealOwner] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSalesRepComparisonOpen, setIsSalesRepComparisonOpen] =
    useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isLimitedToOtherContact, setIsLimitedToOtherContact] = useState(false);
  const [showPhonebookWarning, setShowPhonebookWarning] = useState(false);
  const [nativePhonebookDialog, setNativePhonebookDialog] =
    useState<NativePhonebookDialogState | null>(null);
  const [isNativePhonebookDialogOpen, setIsNativePhonebookDialogOpen] =
    useState(false);
  const [addressOptions, setAddressOptions] = useState<AddressSuggestion[]>([]);
  const [isAddressSearching, setIsAddressSearching] = useState(false);
  const searchRequestId = useRef(0);

  const isEdit = mode === "edit";
  const contactTypeName =
    contactType?.name ||
    draft.type ||
    draft.contactType ||
    editContact?.type ||
    editContact?.contactType ||
    "";
  const normalizedContactType = contactTypeName.toLowerCase();
  const isBuyerSeller = buyerSellerContactTypes.includes(contactTypeName);
  const isSalesPerson = normalizedContactType.includes("sales person");
  const isAccountRep = isAccountRepType(contactTypeName);
  const isAttorney = normalizedContactType.includes("attorney");
  const isPrincipalBuyer = normalizedContactType.includes("principal - buyer");
  const isPrincipalSeller =
    normalizedContactType.includes("principal - seller");
  const isPrincipal =
    isPrincipalBuyer ||
    isPrincipalSeller ||
    isPrincipalContactType(contactTypeName);
  const canEditContactType =
    !isEdit || !contactType || Boolean(contactType.isOther) || isPrincipal;
  const isGovernment = contactTypeName === "Government";
  const isTitleUnderwriter = isTitleUnderwriterType(contactTypeName);
  const selectedUnderwriterLookupCode =
    getUnderwriterLookupCode(selectedUnderwriter);
  const isTitleUnderwriterFlow =
    isTitleUnderwriter ||
    Boolean(
      (selectedTitleCompany?.lookupCode || selectedUnderwriterLookupCode) &&
      isTitleUnderwriterType(
        contactType?.name ||
          draft.type ||
          draft.contactType ||
          editContact?.type ||
          editContact?.contactType,
      ),
    );
  const isPhonebookContact =
    Boolean(contactType?.isPhonebookContact) &&
    !isSalesPerson &&
    !isAccountRep &&
    !isTitleUnderwriterFlow;
  const isInternalUserContact =
    isSalesPerson || isAccountRep || normalizedContactType === "funder";
  const displayEmails = draft.emailsSplit?.length
    ? draft.emailsSplit
    : draft.email
      ? draft.email.split(";")
      : [];
  const titleCompanyStateCode =
    orderInfo?.stateCode ||
    orderInfo?.properties?.find((property) => property.state)?.state;
  const trimmedPhonebookSearchText = phonebookSearchText.trim();
  const phonebookSearchNoOptionsText =
    trimmedPhonebookSearchText.length < 3
      ? "Type at least 3 characters to search"
      : "No matching contacts found";
  const phonebookAutocompleteOptions = useMemo(() => {
    const shouldShowAddNewOption =
      Boolean(trimmedPhonebookSearchText) &&
      !isSearching &&
      !isInternalUserContact &&
      !isGovernment;
    if (!shouldShowAddNewOption) {
      return phonebookOptions;
    }

    return [
      {
        ...emptyContact(),
        id: "__new-phonebook-contact__",
        name: trimmedPhonebookSearchText,
        isNew: true,
      },
      ...phonebookOptions,
    ];
  }, [
    isGovernment,
    isInternalUserContact,
    isSearching,
    phonebookOptions,
    trimmedPhonebookSearchText,
  ]);

  const contactTypeOptions = useMemo(() => {
    if (!isLimitedToOtherContact) {
      return contactTypes;
    }

    return [
      ...contactTypes.filter((type) => type.name === "Other"),
      ...contactTypes.filter((type) => type.name !== "Other" && type.isOther),
    ];
  }, [contactTypes, isLimitedToOtherContact]);

  const representsOptions = useMemo(
    () => [
      { name: "All Buyers", code: "ba", type: "" },
      { name: "All Sellers", code: "sa", type: "" },
      { name: "Middle Party", code: "mp", type: "" },
      { name: "End Buyer", code: "eb", type: "" },
      ...contacts
        .filter((orderContact) => {
          const typeName = (orderContact.type || "").toLowerCase();
          const code = (orderContact.code || "").toLowerCase();

          return (
            orderContact.code &&
            orderContact.name &&
            orderContact.name !== draft.name &&
            !typeName.includes("attorney") &&
            code !== "sa" &&
            code !== "ba" &&
            (typeName.includes("buyer") ||
              typeName.includes("seller") ||
              typeName.includes("lender"))
          );
        })
        .map((orderContact) => ({
          name: orderContact.name || "",
          code: orderContact.code || "",
          type: orderContact.type || "",
        })),
    ],
    [contacts, draft.name],
  );

  const setAttorneyRepresentsField = (
    selectedType: DocumentsContactType,
    startingDraft = emptyContact(),
  ) => {
    const attorneyType =
      contactTypes.find((type) => type.name === "Attorney") || selectedType;
    const selectedName = selectedType?.name || "";
    const normalizedSelectedName = selectedName
      .replace(/['\s]/g, "")
      .toLowerCase();
    const buyerOrSellerAttorney =
      normalizedSelectedName === "buyersattorney" ||
      normalizedSelectedName === "sellersattorney";

    if (buyerOrSellerAttorney) {
      const isBuyer = normalizedSelectedName === "buyersattorney";
      const rep = representsOptions.find(
        (option) => option.name === (isBuyer ? "All Buyers" : "All Sellers"),
      );
      setContactType(attorneyType);
      setDraft({
        ...startingDraft,
        type: selectedName,
        contactType: selectedName,
        represents: rep?.code || (isBuyer ? "ba" : "sa"),
      });
      return true;
    }

    if (normalizedSelectedName === "lendersattorney") {
      const firstLender = representsOptions.find((option) =>
        option.type?.toLowerCase().includes("lender"),
      );
      setContactType(attorneyType);
      setDraft({
        ...startingDraft,
        type: selectedName,
        contactType: selectedName,
        represents: firstLender?.code || "",
      });
      return true;
    }

    return false;
  };

  const handleContactTypeChange = (
    value: DocumentsContactType | null,
    reason?:
      | "createOption"
      | "selectOption"
      | "removeOption"
      | "blur"
      | "clear",
  ) => {
    if (reason === "clear" && contactType?.isOther) {
      setContactType(null);
      setDraft((currentDraft) => ({
        ...currentDraft,
        type: "",
        contactType: "",
      }));
      setIsLimitedToOtherContact(isEdit);
      setPhonebookSearchText("");
      setPhonebookOptions([]);
      return;
    }

    if (reason === "clear") {
      setContactType(null);
      setDraft((currentDraft) => ({
        ...currentDraft,
        type: "",
        contactType: "",
      }));
      setIsLimitedToOtherContact(false);
      setPhonebookSearchText("");
      setPhonebookOptions([]);
      return;
    }

    if (!value) {
      setContactType(null);
      setDraft(emptyContact());
      setIsLimitedToOtherContact(false);
      setPhonebookSearchText("");
      setPhonebookOptions([]);
      return;
    }

    const nextDraft = {
      ...draft,
      type: value.name || "",
      contactType: value.name || "",
    };
    if (!setAttorneyRepresentsField(value, nextDraft)) {
      setContactType(value);
      setDraft(nextDraft);
    }
    setPhonebookSearchText("");
    setPhonebookOptions([]);
    setIsLimitedToOtherContact(false);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    getProxyContactTypes().then(setContactTypes);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const startingContact = editContact ? { ...editContact } : emptyContact();
    startingContact.emailsSplit = startingContact.emailsSplit?.length
      ? startingContact.emailsSplit
      : startingContact.email
        ? startingContact.email.split(";").filter(Boolean)
        : [];
    setDraft(startingContact);
    setSelectedBranch({
      address1: startingContact.address1,
      address2: startingContact.address2,
      city: startingContact.city,
      state: startingContact.state,
      zip: startingContact.zip,
    });
    setBranchOptions([]);
    setPhonebookOptions([]);
    setPhonebookSearchText(getContactName(startingContact));
    setErrorMessage(null);
    setSearchResults(null);
    setSubmitSuccess(false);
    setSelectedAccountRep(null);
    setTitleCompanyOptions([]);
    setUnderwriterOptions([]);
    setSelectedTitleCompany(null);
    setSelectedUnderwriter(null);
    setSalesReps([null, null, null]);
    setIsDeleteConfirmOpen(false);
    setIsSalesRepComparisonOpen(false);
    setShowPhonebookWarning(false);
    setInvalidEmailIndexes([]);
    const startingTypeName =
      editContact?.type || editContact?.contactType || "";
    setContactType(
      startingTypeName
        ? getFallbackContactType(startingTypeName, editContact)
        : null,
    );
    setIsLimitedToOtherContact(
      Boolean(
        isEdit &&
        (normalizedText(startingTypeName) === "other" ||
          editContact?.otherContactType),
      ),
    );

    const buyerSellerInfo = startingContact.buyerSellerInfo;
    setSameAsPropertyAddress(buyerSellerInfo?.sameAsPropertyAddress ?? true);
    setIsOrganization(Boolean(buyerSellerInfo?.organization));
    setIsJoint(Boolean(buyerSellerInfo?.joint));
    setIndividual(buyerSellerInfo?.individual || defaultIndividual());
    setOrganization({
      ...defaultOrganization(),
      ...buyerSellerInfo?.organization,
      isPayeeNameSameAsName: !buyerSellerInfo?.organization?.payeeName,
    });
    setJoint(buyerSellerInfo?.joint || defaultJoint());
    setAddress(
      buyerSellerInfo?.organization?.address ||
        buyerSellerInfo?.individual?.address ||
        buyerSellerInfo?.joint?.jointOne?.address ||
        defaultAddress(),
    );
  }, [editContact, open]);

  useEffect(() => {
    if (!open || !orderNumber) {
      return;
    }

    axios
      .get<string[]>(
        `/api/orders/getClientsOnOrder?orderNumber=${encodeURIComponent(orderNumber)}`,
      )
      .then(({ data }) => {
        const lookupCodes = data ?? [];
        setDealOwners(lookupCodes);
        setIsDealOwner(
          Boolean(
            editContact?.lookupCode &&
            lookupCodes.includes(editContact.lookupCode),
          ),
        );
      })
      .catch(() => {
        setDealOwners([]);
        setIsDealOwner(false);
      });
  }, [editContact?.lookupCode, open, orderNumber]);

  useEffect(() => {
    if (!open || !contactTypes.length) {
      return;
    }

    const typeName = editContact?.type || editContact?.contactType || "";
    const match = contactTypes.find(
      (type) => normalizedText(type.name) === normalizedText(typeName),
    );
    if (match) {
      setContactType(match);
      setIsLimitedToOtherContact(Boolean(isEdit && match.isOther));
    } else if (typeName) {
      const fallbackContactType = getFallbackContactType(typeName, editContact);
      setContactType(fallbackContactType);
      setIsLimitedToOtherContact(
        Boolean(isEdit && fallbackContactType.isOther),
      );
    } else {
      setContactType(null);
      setIsLimitedToOtherContact(false);
    }
  }, [contactTypes, editContact, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    axios
      .get<DocumentsRep[]>("/proxy/api/salesreps/search?searchText=")
      .then(({ data }) => setSalesPersonOptions(data ?? []));
  }, [open]);

  useEffect(() => {
    if (!open || !isSalesPerson) {
      return;
    }

    axios
      .get<
        DocumentsRep[]
      >(`/api/orders/getsalesreps?ordernumber=${encodeURIComponent(orderNumber)}`)
      .then(({ data }) =>
        setSalesReps([data?.[0] || null, data?.[1] || null, data?.[2] || null]),
      );
  }, [isSalesPerson, open, orderNumber]);

  useEffect(() => {
    if (!open || !isAccountRep) {
      return;
    }

    axios
      .get<
        DocumentsRep[]
      >("/proxy/api/clientphonebook/contacts/getwindowsusers")
      .then(({ data }) => {
        const options = uniqueRepsByName(data ?? []);
        setAccountRepOptions(options);
        setSelectedAccountRep((currentSelection) => {
          if (currentSelection) {
            return currentSelection;
          }

          return (
            options.find(
              (option) =>
                repMatches(option, editContact) ||
                repMatches(option, orderInfo?.accountRep),
            ) || null
          );
        });
      });
  }, [editContact, isAccountRep, open, orderInfo?.accountRep]);

  useEffect(() => {
    if (!open || !isTitleUnderwriter || !titleCompanyStateCode) {
      return;
    }

    axios
      .get<
        DocumentsTitleCompany[]
      >(`/proxy/api/titlecompanies?state=${encodeURIComponent(titleCompanyStateCode)}`)
      .then(({ data }) => {
        const options = data ?? [];
        const titleCompanyContacts = [
          ...(isOrderTitleCompanyType(
            editContact?.type || editContact?.contactType,
          )
            ? [editContact]
            : []),
          ...contacts.filter((orderContact) =>
            isOrderTitleCompanyType(
              orderContact.type || orderContact.contactType,
            ),
          ),
        ].filter(Boolean) as DocumentsOrderContact[];
        setTitleCompanyOptions(options);
        setSelectedTitleCompany(
          options.find((option) =>
            titleCompanyContacts.some((orderContact) =>
              contactMatchesTitleCompany(orderContact, option),
            ),
          ) || null,
        );
      });
  }, [contacts, editContact, isTitleUnderwriter, open, titleCompanyStateCode]);

  useEffect(() => {
    if (!selectedTitleCompany?.lookupCode) {
      setUnderwriterOptions([]);
      setSelectedUnderwriter(null);
      return;
    }

    axios
      .get<
        DocumentsUnderwriter[]
      >(`/proxy/api/underwriters/getbytitlecompany?titlecompany=${encodeURIComponent(selectedTitleCompany.lookupCode)}`)
      .then(({ data }) => {
        const options = data ?? [];
        setUnderwriterOptions(options);
        const underwriterContacts = [
          ...(editContact?.type === "Underwriter" ? [editContact] : []),
          ...contacts.filter(
            (orderContact) => orderContact.type === "Underwriter",
          ),
        ];
        setSelectedUnderwriter(
          options.find((option) =>
            underwriterContacts.some((orderContact) =>
              contactMatchesUnderwriter(orderContact, option),
            ),
          ) || null,
        );
      });
  }, [contacts, editContact, selectedTitleCompany?.lookupCode]);

  useEffect(() => {
    if (
      !open ||
      !contactType ||
      !trimmedPhonebookSearchText ||
      trimmedPhonebookSearchText.length < 3
    ) {
      searchRequestId.current += 1;
      setPhonebookOptions([]);
      setIsSearching(false);
      return;
    }

    const requestId = ++searchRequestId.current;
    const abortController = new AbortController();
    setIsSearching(true);
    const timeout = window.setTimeout(async () => {
      try {
        let url = "";
        if (isGovernment) {
          url = `/proxy/api/contacts/SearchGovernmentContacts?searchterm=${encodeURIComponent(trimmedPhonebookSearchText)}&isny=${String((orderInfo?.stateCode || "").toLowerCase() === "ny")}`;
        } else if (normalizedContactType === "funder") {
          url = `/api/clientphonebook/external/searchFunders?searchText=${encodeURIComponent(trimmedPhonebookSearchText)}`;
        } else {
          const actualContactType = isAttorney ? "attorney" : contactType.name;
          url = `/api/clientphonebook/external/searchContactsForMomentum?contactType=${encodeURIComponent(actualContactType)}&searchText=${encodeURIComponent(trimmedPhonebookSearchText)}`;
        }

        const { data } = await axios.get<DocumentsOrderContact[]>(url, {
          signal: abortController.signal,
        });
        if (requestId === searchRequestId.current && !abortController.signal.aborted) {
          setPhonebookOptions(
            (data ?? []).map((option) =>
              isGovernment
                ? normalizeGovernmentContact(option as any)
                : normalizeContact(option),
            ),
          );
        }
      } catch (error) {
        if (axios.isCancel(error) || abortController.signal.aborted) {
          return;
        }
        if (requestId === searchRequestId.current) {
          setPhonebookOptions([]);
        }
      } finally {
        if (requestId === searchRequestId.current && !abortController.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeout);
      abortController.abort();
    };
  }, [
    contactType,
    isAttorney,
    isGovernment,
    normalizedContactType,
    open,
    orderInfo?.stateCode,
    trimmedPhonebookSearchText,
  ]);

  const normalizeContact = (
    orderContact: DocumentsOrderContact,
  ): DocumentsOrderContact => ({
    ...orderContact,
    id: orderContact.id || "",
    isMarketingSource:
      orderContact.isMarketingSource ?? draft.isMarketingSource ?? false,
    emailsSplit: orderContact.emailsSplit?.length
      ? orderContact.emailsSplit
      : orderContact.email
        ? orderContact.email.split(";").filter(Boolean)
        : [],
  });

  const getMomentumPhonebookContactByLookupCode = async (
    lookupCode: string,
    isCompanyFirst: boolean,
  ) => {
    const contactUrl = `/api/clientphonebook/external/getMomentumContactFromPhonebook?lookupCode=${encodeURIComponent(lookupCode)}`;
    const companyUrl = `/api/clientphonebook/external/getMomentumCompanyContactFromPhonebook?lookupCode=${encodeURIComponent(lookupCode)}`;
    const urls = isCompanyFirst
      ? [companyUrl, contactUrl]
      : [contactUrl, companyUrl];

    for (const url of urls) {
      try {
        const { data } = await axios.get<DocumentsOrderContact | null>(url);
        if (data && (data.lookupCode || getContactName(data))) {
          return normalizeContact(data);
        }
      } catch {
        // Try the alternate phonebook endpoint before falling back to order-contact data.
      }
    }

    return null;
  };

  const fetchBranches = async (selectedContact: DocumentsOrderContact) => {
    const fallbackBranch = getBranchFromContact(selectedContact);

    if (
      selectedContact.isIndividual &&
      !isNumericIdentifier(selectedContact.branchId)
    ) {
      setBranchOptions(fallbackBranch ? [fallbackBranch] : []);
      setSelectedBranch(fallbackBranch);
      return;
    }

    if (
      !selectedContact.isIndividual &&
      !isNumericIdentifier(selectedContact.id)
    ) {
      setBranchOptions(fallbackBranch ? [fallbackBranch] : []);
      setSelectedBranch(fallbackBranch);
      return;
    }

    try {
      const url = selectedContact.isIndividual
        ? `/api/clientphonebook/external/getCompanyBranchForMomentum/${selectedContact.branchId}`
        : `/api/clientphonebook/external/getCompanyBranchesForMomentum/${selectedContact.id}`;
      const { data } = await axios.get<
        DocumentsCompanyBranch[] | DocumentsCompanyBranch
      >(url);
      const branches = (Array.isArray(data) ? data : data ? [data] : []).filter(
        (branch) => branch.address1 || branch.address2,
      );
      setBranchOptions(
        branches.length ? branches : fallbackBranch ? [fallbackBranch] : [],
      );
      setSelectedBranch(
        branches.find((branch) => branch.isHeadquarters) ||
          branches[0] ||
          fallbackBranch,
      );
    } catch {
      setBranchOptions(fallbackBranch ? [fallbackBranch] : []);
      setSelectedBranch(fallbackBranch);
    }
  };

  useEffect(() => {
    const editContactTypeName =
      editContact?.type || editContact?.contactType || "";
    if (
      !open ||
      !isEdit ||
      !editContact ||
      isGovernment ||
      isInternalUserContact ||
      isInternalUserContactType(editContactTypeName) ||
      isTitleUnderwriter ||
      isTitleUnderwriterType(editContactTypeName) ||
      (!editContact.lookupCode && !isPhonebookContact)
    ) {
      return;
    }

    let isCancelled = false;

    (async () => {
      const hydratedContact =
        (await getMomentumPhonebookContactByLookupCode(
          editContact.lookupCode || "",
          editContact.isIndividual === false,
        )) || editContact;

      if (isCancelled) {
        return;
      }

      setDraft((currentDraft) => ({
        ...currentDraft,
        ...hydratedContact,
        code: currentDraft.code || editContact.code,
        contactType:
          currentDraft.contactType ||
          editContact.contactType ||
          editContact.type,
        isMarketingSource: currentDraft.isMarketingSource,
        represents: currentDraft.represents || editContact.represents,
        type: currentDraft.type || editContact.type,
      }));
      setPhonebookSearchText(getContactName(hydratedContact));

      await fetchBranches(hydratedContact);
    })().catch(() => {
      if (isCancelled) {
        return;
      }
      const fallbackBranch = {
        address1: editContact.address1,
        address2: editContact.address2,
        city: editContact.city,
        state: editContact.state,
        zip: editContact.zip,
      };
      setSelectedBranch(fallbackBranch);
      setBranchOptions(
        fallbackBranch.address1 || fallbackBranch.address2
          ? [fallbackBranch]
          : [],
      );
    });

    return () => {
      isCancelled = true;
    };
  }, [
    editContact,
    editContact?.branchId,
    editContact?.id,
    editContact?.isIndividual,
    editContact?.lookupCode,
    editContact?.type,
    editContact?.contactType,
    isEdit,
    isGovernment,
    isInternalUserContact,
    isPhonebookContact,
    isTitleUnderwriter,
    open,
  ]);

  const handlePhonebookSave = async (savedContact: DocumentsOrderContact) => {
    const hydratedContact = savedContact.lookupCode
      ? await getMomentumPhonebookContactByLookupCode(
          savedContact.lookupCode,
          savedContact.isIndividual === false,
        )
      : null;
    const phonebookContact = hydratedContact || savedContact;
    const nextContact = normalizeContact({
      ...draft,
      ...phonebookContact,
      isMarketingSource: draft.isMarketingSource,
      type: draft.type || contactTypeName,
      contactType: draft.contactType || contactTypeName,
      represents: draft.represents,
      code: draft.code,
    });

    setDraft(nextContact);
    setPhonebookSearchText(getContactName(nextContact));
    setPhonebookOptions([]);
    if (!isGovernment) {
      await fetchBranches(nextContact);
    }
  };

  const handleNativePhonebookSaved = async (
    phonebookId: number | string | null,
    isCompany: boolean,
  ) => {
    if (!phonebookId) {
      return;
    }

    const endpoint = isCompany
      ? `/api/clientphonebook/companies/getOrderEntryCompanyByPhonebookId?id=${phonebookId}`
      : `/api/clientphonebook/contacts/getOrderEntryContactByPhonebookId?id=${phonebookId}`;
    const { data } = await axios.get<DocumentsOrderContact>(endpoint);
    await handlePhonebookSave(data);
    closeNativePhonebookDialog();
  };

  const openNativePhonebookDialog = (
    dialogState: NativePhonebookDialogState,
  ) => {
    setNativePhonebookDialog(dialogState);
    setIsNativePhonebookDialogOpen(true);
  };

  const closeNativePhonebookDialog = () => {
    setIsNativePhonebookDialogOpen(false);
  };

  const clearNativePhonebookDialog = () => {
    if (!isNativePhonebookDialogOpen) {
      setNativePhonebookDialog(null);
    }
  };

  const getNativePhonebookIdByLookupCode = async (
    lookupCode: string,
    isCompany: boolean,
  ) => {
    try {
      const url = isCompany
        ? `/api/clientphonebook/companies/get?id=&lookupcode=${encodeURIComponent(lookupCode)}`
        : `/api/clientphonebook/contacts/get?id=&lookupcode=${encodeURIComponent(lookupCode)}`;
      const { data } = await axios.get<{ id?: number | string } | null>(url);
      return data?.id || null;
    } catch {
      return null;
    }
  };

  const openExistingPhonebookRecord = async () => {
    const lookupCode = draft.lookupCode || editContact?.lookupCode || "";
    if (!lookupCode) {
      setShowPhonebookWarning(true);
      return;
    }

    const isCompany = draft.isIndividual === false;
    const hydratedContact = await getMomentumPhonebookContactByLookupCode(
      lookupCode,
      isCompany,
    );
    const phonebookId =
      (isNumericIdentifier(draft.id) ? draft.id : null) ||
      hydratedContact?.id ||
      (await getNativePhonebookIdByLookupCode(lookupCode, isCompany));

    if (!phonebookId) {
      setShowPhonebookWarning(true);
      return;
    }

    openNativePhonebookDialog({
      pageView: isCompany ? "edit-company" : "edit-contact",
      id: phonebookId,
    });
  };

  const openNewPhonebookRecord = () => {
    const defaultName = phonebookSearchText || getContactName(draft);
    if (normalizedContactType === "lender") {
      openNativePhonebookDialog({
        pageView: "create-company",
        defaultName,
        defaultCompanyType: contactTypeName,
      });
      return;
    }

    openNativePhonebookDialog({
      pageView: "create-contact",
      defaultName,
    });
  };

  const handleSelectPhonebookContact = async (
    selectedContact: DocumentsOrderContact | null,
  ) => {
    if (!selectedContact) {
      return;
    }

    const nextContact = normalizeContact({
      ...selectedContact,
      isMarketingSource: draft.isMarketingSource,
      type: draft.type || contactTypeName,
      contactType: draft.contactType || contactTypeName,
      represents: draft.represents,
      code: draft.code,
    });
    setDraft(nextContact);
    setPhonebookSearchText(getContactName(nextContact));
    setPhonebookOptions([]);
    if (!isGovernment) {
      await fetchBranches(nextContact);
    }
  };

  const clearSelectedPhonebookContact = () => {
    setPhonebookSearchText("");
    setPhonebookOptions([]);
    setSelectedBranch(null);
    setBranchOptions([]);
    setDraft({
      ...emptyContact(),
      isMarketingSource: draft.isMarketingSource,
      type: draft.type || contactTypeName,
      contactType: draft.contactType || contactTypeName,
      represents: draft.represents,
      code: draft.code,
    });
  };

  const resetPhonebookSearchTextToSelection = () => {
    const selectedName = getContactName(draft);
    if (!draft.lookupCode && !selectedName) {
      return;
    }

    setPhonebookSearchText(
      draft.lookupCode && selectedName ? selectedName : "",
    );
  };

  const validate = () => {
    if (!contactTypeName) {
      setErrorMessage("Please choose a contact type.");
      return false;
    }

    if (isSalesPerson) {
      return true;
    }

    if (isAccountRep && !selectedAccountRep) {
      setErrorMessage("Please choose an account rep.");
      return false;
    }

    if (isAccountRep) {
      return true;
    }

    if (
      isTitleUnderwriterFlow &&
      (!selectedTitleCompany?.lookupCode || !selectedUnderwriterLookupCode)
    ) {
      setErrorMessage("Please choose a title company and underwriter.");
      return false;
    }

    if (isTitleUnderwriterFlow) {
      return true;
    }

    if (isBuyerSeller) {
      if (!validEmail(address.email)) {
        setErrorMessage("Please enter a valid email.");
        return false;
      }
      return true;
    }

    const invalidIndexes = (draft.emailsSplit || [])
      .map((email, index) => (validEmail(email) ? null : index))
      .filter((index): index is number => index !== null);
    setInvalidEmailIndexes(invalidIndexes);
    if (invalidIndexes.length || !validEmail(draft.email)) {
      setErrorMessage("Please enter a valid email.");
      return false;
    }

    return true;
  };

  const buildTransactingParty = (): DocumentsTransactingParty => ({
    sameAsPropertyAddress,
    organization: isOrganization
      ? {
          ...organization,
          payeeName: organization.isPayeeNameSameAsName
            ? organization.name
            : organization.payeeName,
          address,
        }
      : undefined,
    joint: isJoint && !isOrganization ? joint : undefined,
    individual:
      !isOrganization && !isJoint ? { ...individual, address } : undefined,
  });

  const buildContactRequest = () => {
    const phonebookContact = Boolean(draft.lookupCode);
    const branch = selectedBranch || getBranchFromContact(draft);
    const saveContactType = isEdit
      ? draft.type || draft.contactType || contactTypeName
      : draft.otherContactType || contactTypeName;
    const contactToSave = {
      ...draft,
      name: getContactName(draft),
      address1: phonebookContact ? branch?.address1 || "" : draft.address1,
      address2: phonebookContact ? branch?.address2 || "" : draft.address2,
      city: phonebookContact ? branch?.city || "" : draft.city,
      state: phonebookContact ? branch?.state || "" : draft.state,
      zip: phonebookContact ? branch?.zip || "" : draft.zip,
      email: draft.emailsSplit?.length
        ? draft.emailsSplit.filter(Boolean).join(";")
        : draft.email,
      contactType: saveContactType,
      type: draft.type || contactTypeName,
      isOther: isEdit ? undefined : contactType?.isOther,
      emailPolicyTo: draft.emailPolicyTo,
    };

    delete (contactToSave as { id?: string }).id;

    return {
      orderNumber,
      contact: contactToSave,
    };
  };

  const getSaveUrl = () => {
    if (isSalesPerson) {
      return "/api/orders/setSalesReps";
    }
    if (isAccountRep) {
      return "/proxy/api/orders/updateaccountrep";
    }
    if (isTitleUnderwriterFlow) {
      return "/proxy/api/orders/UpdateTitleCompanyAndUnderwriter";
    }
    if (isBuyerSeller) {
      const isBuyer = contactTypeName.includes("Buyer");
      if (isEdit) {
        return `/proxy/api/orders/update${isBuyer ? "buyer" : "seller"}onorder`;
      }
      return `/proxy/api/orders/add${isBuyer ? "buyer" : "seller"}toorder`;
    }
    return `/proxy/api/orders/${isEdit ? "updateordercontact" : "addordercontact"}`;
  };

  const getSavePayload = (updatedSalesRepIds?: string[]) => {
    if (isSalesPerson) {
      return {
        orderNumber,
        salesRepIds: salesReps.map((rep) => rep?.id).filter(Boolean),
      };
    }
    if (isTitleUnderwriterFlow) {
      return {
        orderNumber,
        titleCompanyLookupCode: selectedTitleCompany?.lookupCode || "",
        underwriter: {
          underwriterLookupCode: selectedUnderwriterLookupCode,
          lookupCode: selectedUnderwriter?.lookupCode || "",
        },
      };
    }
    if (isAccountRep) {
      return {
        orderNumber,
        newAccountRepId: selectedAccountRep?.id,
      };
    }
    if (isBuyerSeller) {
      return {
        orderNumber,
        code: editContact?.code,
        transactingParty: buildTransactingParty(),
      };
    }
    return {
      ...buildContactRequest(),
      updatedSalesRepIds,
    };
  };

  const getSearchNames = async () => {
    if (isSalesPerson || isAccountRep || isTitleUnderwriterFlow) {
      return [];
    }
    if (isBuyerSeller && isOrganization) {
      return [
        { name: organization.name, isCompany: true },
        {
          name: organization.isPayeeNameSameAsName
            ? organization.name
            : organization.payeeName,
          isCompany: true,
        },
      ].filter((entry) => entry.name);
    }
    if (isBuyerSeller && isJoint) {
      return [
        { name: getFullName(joint.jointOne), isCompany: false },
        { name: getFullName(joint.jointTwo), isCompany: false },
      ].filter((entry) => entry.name);
    }
    if (isBuyerSeller) {
      return [{ name: getFullName(individual), isCompany: false }].filter(
        (entry) => entry.name,
      );
    }

    const phonebookContactInfo = draft.lookupCode
      ? await getMomentumPhonebookContactByLookupCode(
          draft.lookupCode,
          draft.isIndividual === false,
        )
      : null;
    const contactForSearch = phonebookContactInfo || draft;
    const isIndividual =
      contactForSearch.isIndividual ??
      contactForSearch.name !== contactForSearch.company;
    return [
      {
        name: isIndividual
          ? getFullName(contactForSearch) || getContactName(draft)
          : contactForSearch.name,
        isCompany: !isIndividual,
      },
      {
        name:
          draft.company ||
          draft.payeeName ||
          contactForSearch.company ||
          contactForSearch.payeeName,
        isCompany: true,
      },
    ].filter((entry) => entry.name);
  };

  const runPostSaveSearch = async () => {
    const names = formatNamesForPatriotAndBlacklistSearch(
      await getSearchNames(),
    );
    if (!names.length) {
      return null;
    }

    const { data } = await axios.post<DocumentsBlacklistSearchResults>(
      "/api/orderdocuments/searchforpatriotsandblacklist",
      {
        names,
        orderNumber,
        contactCode: editContact?.code || draft.code,
        contactLookupCode: draft.lookupCode,
        contactName: getContactName(draft) || names[0],
        contactType: contactTypeName,
      },
    );
    setSearchResults(data);
    return data;
  };

  const updateDealOwner = async () => {
    if (!isPrincipal || !draft.lookupCode) {
      return;
    }

    const alreadyDealOwner = dealOwners.includes(draft.lookupCode);
    if (isDealOwner === alreadyDealOwner) {
      return;
    }

    const updatedLookupCodes = isDealOwner
      ? [...dealOwners, draft.lookupCode]
      : dealOwners.filter((lookupCode) => lookupCode !== draft.lookupCode);

    await axios.post("/api/orders/setClientOnOrder", {
      orderNumber,
      lookupCodes: updatedLookupCodes,
    });
    setDealOwners(updatedLookupCodes);
  };

  const isNewMarketingSource = () =>
    Boolean(
      draft.isMarketingSource &&
      draft.lookupCode &&
      !contacts.some(
        (orderContact) =>
          orderContact.isMarketingSource &&
          orderContact.lookupCode === draft.lookupCode,
      ),
    );

  const handleSave = async (updatedSalesRepIds?: string[]) => {
    if (!validate()) {
      return;
    }

    if (
      !updatedSalesRepIds &&
      isNewMarketingSource() &&
      draft.marketingReps?.length
    ) {
      setIsSalesRepComparisonOpen(true);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const { data } = await axios.post(
        getSaveUrl(),
        getSavePayload(updatedSalesRepIds),
      );
      if (data?.errorMessage) {
        setErrorMessage(data.errorMessage);
        return;
      }

      if (isAccountRep) {
        await onSaved();
        logDocumentContactEvent(mode, editContact, draft);
        onClose();
        return;
      }

      const results = await runPostSaveSearch();
      await updateDealOwner();
      await onSaved();
      logDocumentContactEvent(mode, editContact, draft);
      setSubmitSuccess(true);
      setSearchResults(results);
    } catch (error) {
      setErrorMessage(
        getAxiosErrorMessage(error, "Contact could not be saved."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editContact?.code) {
      return;
    }

    setIsDeleting(true);
    setErrorMessage(null);
    try {
      const { data } = await axios.post(
        "/proxy/api/orders/deleteordercontact",
        {
          orderNumber,
          contactCode: editContact.code,
        },
      );
      if (data?.errorMessage) {
        setErrorMessage(data.errorMessage);
        return;
      }
      await onSaved();
      onClose();
    } catch (error) {
      setErrorMessage(
        getAxiosErrorMessage(
          error,
          "Contact could not be removed from the order.",
        ),
      );
    } finally {
      setIsDeleting(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  const updateEmail = (index: number, value: string) => {
    const nextEmails = [...displayEmails];
    nextEmails[index] = value;
    setInvalidEmailIndexes(
      invalidEmailIndexes.filter((invalidIndex) => invalidIndex !== index),
    );
    setDraft({
      ...draft,
      emailsSplit: nextEmails,
      email: nextEmails.join(";"),
    });
  };

  const searchAddressOptions = async (text: string) => {
    if (!text || text.length < 3) {
      setAddressOptions([]);
      return;
    }

    setIsAddressSearching(true);
    try {
      const { data } = await axios.get(
        `/api/properties/search?searchText=${encodeURIComponent(text)}`,
      );
      setAddressOptions(
        (data?.suggestions ?? []).map(
          (suggestion: any) =>
            ({
              entries: suggestion.entries,
              aptNo: suggestion.secondary,
              address: suggestion.street_line,
              formattedAddress:
                suggestion.formattedStreet_line || suggestion.street_line,
              city: suggestion.city,
              state: suggestion.state,
              zipCode: suggestion.zipcode,
            }) as AddressSuggestion,
        ),
      );
    } finally {
      setIsAddressSearching(false);
    }
  };

  const selectAddressOption = (value: AddressSuggestion | null) => {
    if (!value) {
      return;
    }

    setDraft({
      ...draft,
      address1: value.formattedAddress || value.address || "",
      address2: value.aptNo || "",
      city: value.city || "",
      state: value.state || "",
      zip: value.zipCode || "",
    });
  };

  const setSalesRepAt = (index: number, rep: DocumentsRep | null) => {
    const nextReps = [...salesReps];
    nextReps[index] = rep;
    setSalesReps(nextReps);
  };

  const renderContactPreview = (isDisplayEditIcon = true) => {
    const displayName = getContactName(draft);
    const addressLine = selectedBranch?.address1 || draft.address1 || "";
    const address2Line = selectedBranch?.address2 || draft.address2 || "";
    const city = selectedBranch?.city || draft.city;
    const state = selectedBranch?.state || draft.state;
    const zip = selectedBranch?.zip || draft.zip;
    const cityStateZip =
      `${city || ""}${city ? "," : ""} ${state || ""} ${zip || ""}`.trim();
    const marketingRepName =
      draft.marketingRep?.displayName ||
      draft.marketingRep?.fullName ||
      draft.marketingRep?.name ||
      draft.marketingReps
        ?.map((rep) => rep.displayName || rep.fullName || rep.name)
        .filter(Boolean)
        .join(", ");

    if (!displayName) {
      return renderEmptyContactPreview();
    }

    return (
      <ContactPreviewWrap>
        <div className="card-top">
          {isDisplayEditIcon && displayName && (
            <IconButton
              className="edit-icon"
              size="small"
              onClick={openExistingPhonebookRecord}
            >
              <Iconify icon="mdi:open-in-new" width={24} />
            </IconButton>
          )}
          <div className="card-avatar">
            <Iconify
              icon={
                draft.isIndividual === false
                  ? "mdi:office-building"
                  : "mdi:account"
              }
              width={42}
            />
          </div>
        </div>
        <div className="contact-name">{displayName}</div>
        {draft.role && <div className="contact-type">{draft.role}</div>}
        {draft.company && draft.isIndividual && (
          <div className="contact-type">
            <span>at </span>
            <strong>{draft.company}</strong>
          </div>
        )}
        <div className="contact-info">
          <div>
            <Iconify icon="mdi:email-outline" width={18} />
            <span>
              {(displayEmails.length
                ? displayEmails
                : draft.email
                  ? draft.email.split(";")
                  : []
              ).map((email) => (
                <div key={email}>{email}</div>
              ))}
            </span>
          </div>
          <div>
            <Iconify icon="mdi:phone-outline" width={18} />
            <span>{draft.phone || ""}</span>
          </div>
          <div>
            <Iconify icon="mdi:map-marker-outline" width={18} />
            <span>
              <div>{addressLine}</div>
              <div>{address2Line}</div>
              <div>{cityStateZip}</div>
            </span>
          </div>
          {marketingRepName && (
            <div>
              <Iconify icon="mdi:account-tie" width={18} />
              <span>{marketingRepName}</span>
            </div>
          )}
        </div>
      </ContactPreviewWrap>
    );
  };

  const renderEmptyContactPreview = () => (
    <ContactPreviewWrap>
      <div className="card-top" style={{ background: "#8d90bf" }}>
        <div className="card-avatar placeholder">
          <Iconify
            className="placeholder-avatar"
            icon="mdi:account"
            width={42}
          />
        </div>
      </div>
      <div className="contact-name">????</div>
      <div className="contact-info">
        <div>
          <Iconify icon="mdi:email" width={20} />
          <span>--</span>
        </div>
        <div>
          <Iconify icon="mdi:phone" width={20} />
          <span>--</span>
        </div>
        <div>
          <Iconify icon="mdi:map-marker" width={20} />
          <span>--</span>
        </div>
      </div>
    </ContactPreviewWrap>
  );

  const renderNoContact = () => (
    <div className="no-contact-yet">
      {renderEmptyContactPreview()}
      <TextField
        size="small"
        placeholder="Search the phonebook/Lookup..."
        disabled
        sx={{ flex: 1, mt: { xs: 0, sm: 0 }, maxWidth: 600 }}
      />
    </div>
  );

  const renderContactForm = () => {
    if (!contactType) {
      return renderNoContact();
    }

    if (isSalesPerson) {
      return (
        <Grid container spacing={2} marginTop={6}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="center">
              <Box width="50%">
                <Stack spacing={4}>
                  {[0, 1, 2].map((index) => (
                    <Autocomplete
                      key={index}
                      size="small"
                      fullWidth
                      options={salesPersonOptions}
                      value={salesReps[index]}
                      isOptionEqualToValue={(option, value) =>
                        Boolean(value) &&
                        getRepLabel(option) === getRepLabel(value)
                      }
                      getOptionLabel={getRepLabel}
                      onChange={(_, value) => setSalesRepAt(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={`Sales Person ${index + 1}`}
                          placeholder="Start typing..."
                        />
                      )}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      );
    }

    if (isAccountRep) {
      return renderAccountRepForm();
    }

    if (isTitleUnderwriter) {
      return (
        <Grid container spacing={2} marginTop={1} marginBottom={1}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="center">
              <Box width="50%">
                <Stack spacing={4}>
                  <TextField
                    fullWidth
                    disabled
                    size="small"
                    label="Contact Type"
                    value={contactTypeName}
                  />
                  <Autocomplete
                    size="small"
                    fullWidth
                    disabled
                    options={titleCompanyOptions}
                    value={selectedTitleCompany}
                    isOptionEqualToValue={(option, value) =>
                      option.lookupCode === value.lookupCode
                    }
                    getOptionLabel={(option) => option.name || ""}
                    onChange={(_, value) => {
                      setErrorMessage(null);
                      setSelectedUnderwriter(null);
                      setSelectedTitleCompany(value);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Title Company" />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    fullWidth
                    disabled={!selectedTitleCompany?.lookupCode}
                    options={underwriterOptions}
                    value={selectedUnderwriter}
                    isOptionEqualToValue={(option, value) =>
                      option.lookupCode === value.lookupCode ||
                      option.underwriterLookupCode ===
                        value.underwriterLookupCode
                    }
                    getOptionLabel={(option) => option.payeeName || ""}
                    onChange={(_, value) => {
                      setErrorMessage(null);
                      setSelectedUnderwriter(value);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" label="Underwriter" />
                    )}
                  />
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      );
    }

    if (isBuyerSeller) {
      return renderBuyerSellerForm();
    }

    if (isGovernment) {
      return renderGovernmentForm();
    }

    if (isPhonebookContact) {
      return renderPhonebookForm();
    }

    return renderManualContactForm();
  };

  const renderAccountRepForm = () => (
    <div className="df full-width mt5 jcsb">
      {renderContactPreview(false)}
      <FormWrapStyles>
        <div className="text-input_wrap full_width">
          <Autocomplete
            size="small"
            fullWidth
            options={accountRepOptions}
            value={selectedAccountRep}
            isOptionEqualToValue={(option, value) =>
              Boolean(value) &&
              (option.id === value.id ||
                normalizedText(getRepLabel(option)) ===
                  normalizedText(getRepLabel(value)))
            }
            getOptionLabel={getRepLabel}
            onChange={(_, value) => {
              setErrorMessage(null);
              setSelectedAccountRep(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Account Rep"
                placeholder="Start typing..."
              />
            )}
          />
        </div>
      </FormWrapStyles>
    </div>
  );

  const renderGovernmentPreview = () => {
    const hasContact = Boolean(getContactName(draft));
    const cityStateZip = [draft.city, draft.state, draft.zip]
      .filter(Boolean)
      .join(", ");

    return (
      <ContactPreviewWrap>
        <div
          className="card-top"
          style={{ background: hasContact ? "#141368" : "#8d90bf" }}
        >
          <div className="card-avatar">
            <Iconify icon="mdi:account-circle-outline" width={64} />
          </div>
        </div>
        <div className="contact-name">{getContactName(draft) || "????"}</div>
        {draft.payeeName !== getContactName(draft) && (
          <div className="contact-type">{draft.payeeName}</div>
        )}
        <div className="contact-info">
          <div>
            <Iconify icon="mdi:phone" width={20} />
            <span>{draft.phone || "--"}</span>
          </div>
          <div>
            <Iconify icon="mdi:fax" width={20} />
            <span>{draft.fax || "--"}</span>
          </div>
          <div>
            <Iconify icon="mdi:map-marker" width={20} />
            <span>
              {hasContact
                ? [draft.address1, draft.address2, cityStateZip]
                    .filter(Boolean)
                    .join(", ")
                : "--"}
            </span>
          </div>
        </div>
      </ContactPreviewWrap>
    );
  };

  const renderGovernmentForm = () => (
    <div className="df full-width mt5 jcsb">
      {renderGovernmentPreview()}
      <Autocomplete
        sx={{ marginLeft: 2 }}
        autoHighlight
        disableClearable
        fullWidth
        loading={isSearching}
        loadingText="Searching phonebook..."
        noOptionsText={phonebookSearchNoOptionsText}
        value={draft.name ? draft : undefined}
        inputValue={phonebookSearchText}
        options={phonebookOptions}
        filterOptions={(options) => options}
        onInputChange={(_, value, reason) => {
          setPhonebookSearchText(value);
          if (reason === "clear") {
            setDraft({
              ...emptyContact(),
              type: "Government",
              contactType: "Government",
            });
          }
        }}
        onChange={(_, value) => {
          if (value && typeof value === "object") {
            const nextContact = {
              ...normalizeGovernmentContact(value as any),
              code: draft.code,
            };
            setDraft(nextContact);
            setPhonebookSearchText(getContactName(nextContact));
          }
        }}
        getOptionLabel={(option) => getContactName(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Contact"
            placeholder="Start typing..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.lookupCode || option.name}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {option.name}
              </Typography>
              <Typography variant="caption">
                {[option.address1, option.phone, option.company]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            </Stack>
          </Box>
        )}
      />
    </div>
  );

  const renderPhonebookForm = () => (
    <>
      {isAttorney && (
        <Box sx={{ width: "100%", mb: 2, maxWidth: 300 }}>
          <Autocomplete
            size="small"
            options={representsOptions}
            value={
              representsOptions.find(
                (option) => option.code === draft.represents,
              ) || null
            }
            getOptionLabel={(option) =>
              option.type ? `${option.type} - ${option.name}` : option.name
            }
            onChange={(_, value) =>
              setDraft({ ...draft, represents: value?.code || "" })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Represents"
                placeholder="Start typing..."
              />
            )}
          />
        </Box>
      )}
      <div className="df full-width mt5 jcsb">
        {renderContactPreview(!isGovernment)}
        <FormWrapStyles>
          <Autocomplete
            size="small"
            openOnFocus
            PopperComponent={PhonebookAutocompletePopper}
            options={phonebookAutocompleteOptions}
            value={draft.name ? draft : null}
            loading={isSearching}
            loadingText="Searching phonebook..."
            noOptionsText={phonebookSearchNoOptionsText}
            inputValue={phonebookSearchText}
            filterOptions={(options) => options}
            getOptionLabel={(option) => getContactName(option)}
            onInputChange={(_, value, reason) => {
              if (reason === "clear") {
                clearSelectedPhonebookContact();
                return;
              }
              if (reason !== "reset") {
                setPhonebookSearchText(value);
              }
            }}
            onChange={(_, value, reason) => {
              if (reason === "clear" || !value) {
                clearSelectedPhonebookContact();
                return;
              }
              if (value?.isNew) {
                return;
              }

              handleSelectPhonebookContact(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Search for ${contactTypeName}...`}
                placeholder="Search the phonebook/Lookup..."
                onBlur={resetPhonebookSearchTextToSelection}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) =>
              option.isNew ? (
                <Box
                  component="li"
                  {...props}
                  key="add-new-phonebook-contact"
                  sx={{ p: "8px 16px" }}
                >
                  <Button size="small"
                    sx={{
                      textTransform: "lowercase",
                      display: "inline-flex",
                      width: "100%",
                      margin: "0 auto",
                    }}
                    fullWidth
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      openNewPhonebookRecord();
                    }}
                    variant="outlined"
                  >
                    Add "
                    <span style={{ color: "#4440DB" }}>
                      {phonebookSearchText}
                    </span>
                    " to Phonebook
                  </Button>
                </Box>
              ) : (
                <Box
                  component="li"
                  {...props}
                  key={`${option.lookupCode}-${option.id}`}
                  sx={{ width: "100%" }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box sx={{ minWidth: 0, pr: 2 }}>
                      <Stack direction="row" spacing={2}>
                        <Iconify
                          icon={
                            option.isIndividual === false
                              ? "mdi:office-building"
                              : "mdi:account"
                          }
                          width={24}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {getContactName(option)}{" "}
                            {option.isIndividual !== undefined && (
                              <Typography component="span" variant="caption">
                                {option.isIndividual
                                  ? "(Individual)"
                                  : "(Company)"}
                              </Typography>
                            )}
                            {option.preferredName && (
                              <Typography
                                sx={{ ml: 0.5 }}
                                variant="body2"
                                color="text.secondary"
                                component="span"
                              >
                                {option.preferredName}
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={300}>
                            {option.email || ""}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={300}>
                            {option.phone || ""}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box
                      textAlign="right"
                      sx={{ flexShrink: 0, maxWidth: 260 }}
                    >
                      <Typography variant="subtitle2" fontWeight={300}>
                        {option.isIndividual && option.role
                          ? `${option.role} ${option.company ? "at" : ""}`
                          : ""}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={300}>
                        {option.company || ""}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={300}>
                        {option.address1 || ""}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )
            }
          />
          {normalizedContactType === "lender" && (
            <TextField
              fullWidth
              size="small"
              label="Lender Name Override"
              sx={{ mt: 2 }}
              value={draft.name || ""}
              onChange={(event) =>
                setDraft({ ...draft, name: event.target.value })
              }
            />
          )}
          <Autocomplete
            size="small"
            options={
              branchOptions.length
                ? branchOptions
                : selectedBranch
                  ? [selectedBranch]
                  : []
            }
            value={selectedBranch}
            getOptionLabel={(option) =>
              option?.address1
                ? `${option.address1}${option.isHeadquarters && branchOptions.length > 1 ? " (Headquarters)" : ""}`
                : ""
            }
            onChange={(_, value) => setSelectedBranch(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Address"
                placeholder="Choose an address..."
              />
            )}
            sx={{ mt: 2 }}
          />
          {displayEmails.length > 0 && renderEmailFields(true)}
        </FormWrapStyles>
      </div>
      {isPrincipal && (
        <Box sx={{ width: "100%", textAlign: "left", mt: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isDealOwner}
                onChange={(event) => setIsDealOwner(event.target.checked)}
              />
            }
            label="Mark as Deal Owner"
          />
        </Box>
      )}
    </>
  );

  const renderManualContactForm = () => (
    <Grid container spacing={2} marginTop={1} marginBottom={1}>
      <Grid item xs={contactTypeName !== "Other" ? 12 : 6}>
        <Stack direction="row" justifyContent="center">
          <Box width={contactTypeName !== "Other" ? "50%" : "100%"}>
            <Autocomplete
              size="small"
              fullWidth
              options={contactType && !isPrincipal ? [] : contactTypeOptions}
              value={contactType}
              getOptionLabel={getContactTypeLabel}
              onChange={(_, value, reason) => {
                handleContactTypeChange(value, reason);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Contact Type"
                  placeholder="Start typing..."
                />
              )}
            />
          </Box>
        </Stack>
      </Grid>
      {(contactTypeName === "Other" || draft.otherContactType) && (
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="Other Type"
            placeholder="Please describe type of other"
            value={draft.otherContactType || ""}
            onChange={(event) =>
              setDraft({ ...draft, otherContactType: event.target.value })
            }
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <Stack direction="row" justifyContent="center">
          <OtherContactImg />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack spacing={2}>
          <Autocomplete
            fullWidth
            freeSolo
            disableClearable
            options={phonebookOptions}
            filterOptions={(options) => options}
            loading={isSearching}
            loadingText="Searching phonebook..."
            noOptionsText={phonebookSearchNoOptionsText}
            inputValue={draft.name || ""}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name || ""
            }
            onInputChange={(_, value, reason) => {
              setDraft({ ...draft, name: value });
              if (reason !== "reset") {
                setPhonebookSearchText(value);
              }
            }}
            onChange={(_, value) => {
              if (!value || typeof value === "string") {
                return;
              }
              setDraft({
                ...draft,
                ...normalizeContact(value),
                code: draft.code,
                isMarketingSource: draft.isMarketingSource,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Name"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={`${option.lookupCode}-${option.name}`}
              >
                <Stack sx={{ width: "100%" }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption">
                    {[
                      option.email,
                      option.address1,
                      option.phone,
                      option.company,
                    ]
                      .filter(Boolean)
                      .join(" | ")}
                  </Typography>
                </Stack>
              </Box>
            )}
          />
          <TextField
            fullWidth
            size="small"
            label="Company / Payee Name"
            value={draft.payeeName || draft.name || ""}
            onChange={(event) =>
              setDraft({ ...draft, payeeName: event.target.value })
            }
          />
          <TextField
            fullWidth
            size="small"
            label="Email"
            value={draft.email || ""}
            error={invalidEmailIndexes.includes(0)}
            helperText={invalidEmailIndexes.includes(0) ? "Invalid Email" : ""}
            onChange={(event) =>
              setDraft({
                ...draft,
                email: event.target.value,
                emailsSplit: [event.target.value],
              })
            }
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          freeSolo
          fullWidth
          loading={isAddressSearching}
          options={addressOptions}
          value={draft.address1 || ""}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : `${option.formattedAddress || option.address || ""}${option.aptNo ? ` ${option.aptNo}` : ""}${
                  option.city
                    ? `, ${option.city}, ${option.state} ${option.zipCode}`
                    : ""
                }`
          }
          onInputChange={(_, value, reason) => {
            setDraft({ ...draft, address1: value });
            if (reason !== "reset") {
              searchAddressOptions(value);
            }
          }}
          onChange={(_, value) => {
            if (typeof value === "object") {
              selectAddressOption(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Address Quick Entry"
              placeholder="Start typing an address..."
              size="small"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Address 2"
          value={draft.address2 || ""}
          onChange={(event) =>
            setDraft({ ...draft, address2: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="City"
          value={draft.city || ""}
          onChange={(event) => setDraft({ ...draft, city: event.target.value })}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          fullWidth
          size="small"
          label="State"
          value={draft.state || ""}
          onChange={(event) =>
            setDraft({ ...draft, state: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          fullWidth
          size="small"
          label="Zip"
          value={draft.zip || ""}
          onChange={(event) => setDraft({ ...draft, zip: event.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Phone"
          value={draft.phone || ""}
          onChange={(event) =>
            setDraft({ ...draft, phone: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Cell"
          value={draft.cell || ""}
          onChange={(event) => setDraft({ ...draft, cell: event.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Fax"
          value={draft.fax || ""}
          onChange={(event) => setDraft({ ...draft, fax: event.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="License Number"
          value={draft.licenseNumber || ""}
          onChange={(event) =>
            setDraft({ ...draft, licenseNumber: event.target.value })
          }
        />
      </Grid>
    </Grid>
  );

  const renderEmailFields = (disablePrimary = false) => (
    <AdditionalEmailsWrap>
      <div className="heading">Emails</div>
      {(displayEmails.length ? displayEmails : [""]).map((email, index) => (
        <div key={index} className="email-row">
          <TextField
            fullWidth
            size="small"
            disabled={disablePrimary && index === 0}
            label={index === 0 ? "Email" : `CC ${index}`}
            value={email}
            error={invalidEmailIndexes.includes(index)}
            helperText={
              invalidEmailIndexes.includes(index) ? "Invalid email" : ""
            }
            onChange={(event) => updateEmail(index, event.target.value)}
          />
          <IconButton
            size="small"
            disabled={index === 0}
            onClick={() => {
              const nextEmails = displayEmails.filter(
                (_, emailIndex) => emailIndex !== index,
              );
              setDraft({
                ...draft,
                emailsSplit: nextEmails,
                email: nextEmails.join(";"),
              });
            }}
          >
            <Iconify icon="mdi:trash-can-outline" width={18} />
          </IconButton>
        </div>
      ))}
      <div
        className="add-button"
        onClick={() =>
          setDraft({
            ...draft,
            emailsSplit: [...displayEmails, ""],
            email: displayEmails.join(";"),
          })
        }
      >
        <Iconify icon="mdi:plus" width={20} />
        <div>Add a CC</div>
      </div>
    </AdditionalEmailsWrap>
  );

  const renderBuyerSellerForm = () => (
    <Box sx={{ width: "100%" }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 4, ml: 1 }}>
        {contactTypeName}
      </Typography>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} sm={6} sx={{ paddingTop: 16 }}>
          <ToggleButtonWrap>
            <button
              className={!isOrganization ? "active" : ""}
              type="button"
              onClick={() => setIsOrganization(false)}
            >
              Individual
            </button>
            <button
              className={isOrganization ? "active" : ""}
              type="button"
              onClick={() => {
                setIsOrganization(true);
                setIsJoint(false);
              }}
            >
              Organization
            </button>
          </ToggleButtonWrap>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          {!isOrganization && (
            <FormControlLabel
              sx={{ marginRight: 0 }}
              control={
                <Switch
                  checked={isJoint}
                  onChange={() => setIsJoint((current) => !current)}
                />
              }
              label="Is Joint"
            />
          )}
        </Grid>
        <Grid item xs={12}>
          {isOrganization
            ? renderOrganizationFields()
            : isJoint
              ? renderJointFields()
              : renderIndividualFields()}
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            checked={sameAsPropertyAddress}
            onChange={() => setSameAsPropertyAddress((current) => !current)}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          <Typography variant="body2">Address same as property</Typography>
        </Grid>
        {!sameAsPropertyAddress && (
          <Grid item xs={12}>
            {renderAddressFields()}
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Phone"
            value={address.phone}
            onChange={(event) =>
              setAddress({ ...address, phone: event.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            value={address.email}
            onChange={(event) =>
              setAddress({ ...address, email: event.target.value })
            }
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderIndividualFields = () => (
    <Grid container spacing={2}>
      {(["firstName", "middleName", "lastName"] as const).map((field) => (
        <Grid item xs={12} md={4} key={field}>
          <TextField
            fullWidth
            size="small"
            label={
              field === "firstName"
                ? "First Name"
                : field === "middleName"
                  ? "Middle Name"
                  : "Last Name"
            }
            value={individual[field] || ""}
            onChange={(event) =>
              setIndividual({ ...individual, [field]: event.target.value })
            }
          />
        </Grid>
      ))}
      <Grid item xs={12} md={6}>
        <ToggleButtonWrap>
          {["Male", "Female"].map((gender) => (
            <button
              key={gender}
              className={individual.gender === gender ? "active" : ""}
              type="button"
              onClick={() => setIndividual({ ...individual, gender })}
            >
              {gender}
            </button>
          ))}
        </ToggleButtonWrap>
      </Grid>
    </Grid>
  );

  const renderOrganizationFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={organization.isPayeeNameSameAsName}
              onChange={(event) =>
                setOrganization({
                  ...organization,
                  isPayeeNameSameAsName: event.target.checked,
                })
              }
            />
          }
          label="Payee same as name"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Name"
          value={organization.name}
          onChange={(event) =>
            setOrganization({ ...organization, name: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          size="small"
          options={organizationTypeOptions}
          value={organization.organizationType || ""}
          onChange={(_, value) =>
            setOrganization({ ...organization, organizationType: value || "" })
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Organization Type" />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Payee Name"
          value={
            organization.isPayeeNameSameAsName
              ? organization.name
              : organization.payeeName
          }
          disabled={organization.isPayeeNameSameAsName}
          onChange={(event) =>
            setOrganization({ ...organization, payeeName: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          size="small"
          options={vestingOptions}
          value={organization.vesting || ""}
          onChange={(_, value) =>
            setOrganization({ ...organization, vesting: value || "" })
          }
          renderInput={(params) => <TextField {...params} size="small" label="Vested In" />}
        />
      </Grid>
    </Grid>
  );

  const renderJointFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          size="small"
          options={jointRelationshipOptions}
          value={joint.relationshipType || ""}
          onChange={(_, value) =>
            setJoint({ ...joint, relationshipType: value || "" })
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Joint Relationship" />
          )}
        />
      </Grid>
      {(["jointOne", "jointTwo"] as const).map((jointKey, index) => (
        <Grid item xs={12} md={6} key={jointKey}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Joint {index + 1}</Typography>
            {(["firstName", "middleName", "lastName"] as const).map((field) => (
              <TextField
                key={field}
                fullWidth
                size="small"
                label={
                  field === "firstName"
                    ? "First Name"
                    : field === "middleName"
                      ? "Middle Name"
                      : "Last Name"
                }
                value={joint[jointKey][field] || ""}
                onChange={(event) =>
                  setJoint({
                    ...joint,
                    [jointKey]: {
                      ...joint[jointKey],
                      [field]: event.target.value,
                    },
                  })
                }
              />
            ))}
            <ToggleButtonWrap>
              {["Male", "Female"].map((gender) => (
                <button
                  key={gender}
                  className={joint[jointKey].gender === gender ? "active" : ""}
                  type="button"
                  onClick={() =>
                    setJoint({
                      ...joint,
                      [jointKey]: { ...joint[jointKey], gender },
                    })
                  }
                >
                  {gender}
                </button>
              ))}
            </ToggleButtonWrap>
          </Stack>
        </Grid>
      ))}
    </Grid>
  );

  const renderAddressFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Address"
          value={address.address1}
          onChange={(event) =>
            setAddress({ ...address, address1: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Address 2"
          value={address.address2}
          onChange={(event) =>
            setAddress({ ...address, address2: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          size="small"
          label="City"
          value={address.city}
          onChange={(event) =>
            setAddress({ ...address, city: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField
          fullWidth
          size="small"
          label="State"
          value={address.state}
          onChange={(event) =>
            setAddress({ ...address, state: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <TextField
          fullWidth
          size="small"
          label="Zip"
          value={address.zip}
          onChange={(event) =>
            setAddress({ ...address, zip: event.target.value })
          }
        />
      </Grid>
    </Grid>
  );

  const blacklistMatches = searchResults?.blackListResults || [];
  const patriotMatches = searchResults?.patriotSearchResults || [];
  const hasBlacklistMatches = blacklistMatches.length > 0;
  const hasPatriotMatches = patriotMatches.length > 0;
  const canShowSuccessPanel =
    submitSuccess && !hasBlacklistMatches && !hasPatriotMatches;
  const blacklistNames = blacklistMatches
    .map(
      (match: DocumentsBlackListResult, index) =>
        `${index + 1}) ${match.information || match.message || ""}`,
    )
    .filter(Boolean)
    .join(", ");
  const patriotNames = patriotMatches
    .map((name, index) => `${index + 1}) ${name}`)
    .join(", ");

  if (!open) {
    return null;
  }

  const renderHeaderText = () => {
    if (isTitleUnderwriter) {
      return "Edit Title Agent & Underwriter";
    }
    if (isGovernment) {
      return "Add Government Contact";
    }
    if (isEdit) {
      return "Edit Contact";
    }
    return "Add Contact to Order";
  };

  return (
    <>
      <MomentumRoot>
        <div className="tile">
          <div className="tile_header_wrap">
            <div className="tile_header">{renderHeaderText()}</div>
          </div>
          {errorMessage && (
            <ErrorMessageWrap>
              <h2>Error: {errorMessage}</h2>
            </ErrorMessageWrap>
          )}
          <Box className={isAttorney ? "df jcsb f-wrap" : "df jcc f-wrap"}>
            <Box sx={{ width: "100%", maxWidth: 320, mb: 2 }}>
              {(contactType?.isPhonebookContact ||
                isInternalUserContact ||
                !contactType) && (
                <Autocomplete
                  size="small"
                  options={contactType && !isPrincipal ? [] : contactTypeOptions}
                  value={contactType}
                  disabled={!canEditContactType}
                  getOptionLabel={getContactTypeLabel}
                  onChange={(_, value, reason) =>
                    handleContactTypeChange(value, reason)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus={!isEdit && !contactType}
                      size="small"
                      label="Contact Type"
                      placeholder="Start typing..."
                    />
                  )}
                />
              )}
            </Box>
            {renderContactForm()}
          </Box>
          {!isSalesPerson &&
            !isAccountRep &&
            !isTitleUnderwriter &&
            !isGovernment && (
              <Box marginTop={1} marginBottom={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(draft.isMarketingSource)}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          isMarketingSource: event.target.checked,
                        })
                      }
                    />
                  }
                  label="Marketing Source"
                />
              </Box>
            )}
          {submitSuccess && hasBlacklistMatches && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">
                  DO NOT PROCEED! Blacklists matches found: {blacklistNames}.{" "}
                  <span style={{ fontWeight: "bold", color: "red" }}>
                    DO NOT CONTINUE{" "}
                  </span>{" "}
                  transaction until confirmed in writing with your manager!
                </Typography>
                <Box>
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={() =>
                      setSearchResults({
                        ...(searchResults || {}),
                        blackListResults: [],
                      } as DocumentsBlacklistSearchResults)
                    }
                    sx={{ textTransform: "unset", width: 100 }}
                  >
                    I understand
                  </Button>
                </Box>
              </Stack>
            </Alert>
          )}
          {submitSuccess && hasPatriotMatches && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">
                  Patriot search matches found: {patriotNames}!!{" "}
                  <span style={{ fontWeight: "bold", color: "red" }}>
                    DO NOT CONTINUE{" "}
                  </span>
                  transaction until confirmed to be not a match.
                </Typography>
                <Box>
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={() =>
                      setSearchResults({
                        ...(searchResults || {}),
                        patriotSearchResults: [],
                      } as DocumentsBlacklistSearchResults)
                    }
                    sx={{ textTransform: "unset", width: 100 }}
                  >
                    I understand
                  </Button>
                </Box>
              </Stack>
            </Alert>
          )}
          {canShowSuccessPanel && (
            <SuccessWrap>
              <div className="df aic gap2">
                <Iconify icon="mdi:check-circle" width={22} />
                <div>
                  {isEdit
                    ? "Contact was updated to order successfully"
                    : "Contact was added to order successfully"}
                </div>
              </div>
              <button onClick={onClose} type="button">
                Close
              </button>
            </SuccessWrap>
          )}
          {!submitSuccess && (
            <ButtonsWrap>
              <Box>
                {isEdit && !isSalesPerson && !isTitleUnderwriter && (
                  <>
                    <Button size="small"
                      color="warning"
                      variant="contained"
                      startIcon={
                        <Iconify icon="mdi:trash-can-outline" width={18} />
                      }
                      onClick={() => setIsDeleteConfirmOpen(true)}
                      disabled={
                        isSubmitting || isDeleting || !editContact?.code
                      }
                      sx={{
                        bgcolor: "#ee765c",
                        ":hover": { bgcolor: "#dc6347" },
                      }}
                    >
                      Remove from Order
                    </Button>
                    <DeleteConfirmWrap show={isDeleteConfirmOpen}>
                      <div className="comingSoonInner">
                        <div className="coming-soon">Are you sure?</div>
                        <div>
                          <span style={{ fontWeight: "bold" }}>
                            {getContactName(editContact)}
                          </span>
                          {editContact?.type && (
                            <span> - {editContact.type} - </span>
                          )}
                          will be deleted from the order.
                        </div>
                        <div
                          className="df gap1"
                          style={{ justifyContent: "flex-end", marginTop: 24 }}
                        >
                          <Button size="small"
                            onClick={() => setIsDeleteConfirmOpen(false)}
                            disabled={isDeleting}
                          >
                            cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={handleDelete}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <CircularProgress size={18} />
                            ) : (
                              "Okay"
                            )}
                          </Button>
                        </div>
                      </div>
                    </DeleteConfirmWrap>
                  </>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small"
                  onClick={onClose}
                  disabled={isSubmitting || isDeleting}
                  sx={{ color: "#141368" }}
                >
                  Cancel
                </Button>
                <Button size="small"
                  variant="contained"
                  onClick={() => handleSave()}
                  disabled={
                    isSubmitting ||
                    isDeleting ||
                    (isEdit && !draft?.code && !isSalesPerson && !isAccountRep)
                  }
                  sx={{ bgcolor: "#141368", ":hover": { bgcolor: "#0f0e52" } }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : isAccountRep || isEdit ? (
                    "Save"
                  ) : (
                    "Add"
                  )}
                </Button>
              </Stack>
            </ButtonsWrap>
          )}
          {showPhonebookWarning &&
            !isTitleUnderwriter &&
            !isSalesPerson &&
            !isAccountRep && (
              <PhonebookWarningWrap show>
                <div className="warning-inner">
                  <div className="warning-title">No Lookup Code Found!</div>
                  <div>
                    The contact in the order does not have a lookup code and
                    therefore is not associated with a phonebook contact. To
                    connect the order contact with the phonebook, press the X
                    icon in the right side of the "search for ..." field and
                    choose a contact from the phonebook.
                  </div>
                  <Button size="small"
                    className="ok-button"
                    variant="contained"
                    onClick={() => setShowPhonebookWarning(false)}
                  >
                    Ok
                  </Button>
                </div>
              </PhonebookWarningWrap>
            )}
        </div>
      </MomentumRoot>
      <Dialog
        open={isNativePhonebookDialogOpen}
        onClose={closeNativePhonebookDialog}
        TransitionProps={{ onExited: clearNativePhonebookDialog }}
        fullWidth
        maxWidth="xl"
      >
        <DialogContent dividers sx={{ p: 3 }}>
          {nativePhonebookDialog?.pageView === "create-contact" && (
            <AddEditContactProvider
              embeddedOptions={{
                mode: "create",
                defaultName: nativePhonebookDialog.defaultName,
                onCancel: closeNativePhonebookDialog,
                onSaved: ({ phonebookId }) =>
                  handleNativePhonebookSaved(phonebookId, false),
              }}
            >
              <AddEditContactForm />
            </AddEditContactProvider>
          )}
          {nativePhonebookDialog?.pageView === "edit-contact" && (
            <AddEditContactProvider
              embeddedOptions={{
                mode: "edit",
                id: nativePhonebookDialog.id,
                onCancel: closeNativePhonebookDialog,
                onSaved: ({ phonebookId }) =>
                  handleNativePhonebookSaved(phonebookId, false),
              }}
            >
              <AddEditContactForm />
            </AddEditContactProvider>
          )}
          {nativePhonebookDialog?.pageView === "create-company" && (
            <AddEditContactProvider
              embeddedOptions={{
                mode: "create",
                defaultName: nativePhonebookDialog.defaultName,
                defaultCompanyType: nativePhonebookDialog.defaultCompanyType,
                onCancel: closeNativePhonebookDialog,
                onCompanySaved: (phonebookId) =>
                  handleNativePhonebookSaved(phonebookId, true),
              }}
            >
              <AddCompanyPage />
            </AddEditContactProvider>
          )}
          {nativePhonebookDialog?.pageView === "edit-company" && (
            <AddEditCompanyProvider
              embeddedOptions={{
                id: nativePhonebookDialog.id,
                onCancel: closeNativePhonebookDialog,
                onSaved: ({ phonebookId }) =>
                  handleNativePhonebookSaved(phonebookId, true),
              }}
            >
              <AddEditCompany />
            </AddEditCompanyProvider>
          )}
        </DialogContent>
      </Dialog>
      <SalesRepComparisonDialog
        open={isSalesRepComparisonOpen}
        orderNumber={orderNumber}
        salesPersonOptions={salesPersonOptions}
        contactSalesReps={draft.marketingReps || []}
        onClose={() => setIsSalesRepComparisonOpen(false)}
        onSubmit={(selectedReps) => {
          setIsSalesRepComparisonOpen(false);
          handleSave(
            selectedReps
              .map((rep) => rep?.id)
              .filter((id): id is string => Boolean(id)),
          );
        }}
      />
    </>
  );
}

function SalesRepComparisonDialog({
  open,
  orderNumber,
  salesPersonOptions,
  contactSalesReps,
  onClose,
  onSubmit,
}: {
  open: boolean;
  orderNumber: string;
  salesPersonOptions: DocumentsRep[];
  contactSalesReps: DocumentsRep[];
  onClose: () => void;
  onSubmit: (reps: (DocumentsRep | null)[]) => void;
}) {
  const [orderSalesReps, setOrderSalesReps] = useState<(DocumentsRep | null)[]>(
    [null, null, null],
  );
  const [selectedContactSalesReps, setSelectedContactSalesReps] = useState<
    (DocumentsRep | null)[]
  >([null, null, null]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedContactSalesReps([
      contactSalesReps?.[0] || null,
      contactSalesReps?.[1] || null,
      contactSalesReps?.[2] || null,
    ]);

    axios
      .get<DocumentsRep[]>(
        `/api/orders/getsalesreps?ordernumber=${encodeURIComponent(orderNumber)}`,
      )
      .then(({ data }) =>
        setOrderSalesReps([
          data?.[0] || null,
          data?.[1] || null,
          data?.[2] || null,
        ]),
      )
      .catch(() => setOrderSalesReps([null, null, null]));
  }, [contactSalesReps, open, orderNumber]);

  const setRepAt = (
    reps: (DocumentsRep | null)[],
    setReps: (reps: (DocumentsRep | null)[]) => void,
    index: number,
    rep: DocumentsRep | null,
  ) => {
    const nextReps = [...reps];
    nextReps[index] = rep;
    setReps(nextReps);
  };

  const renderRepForm = (
    reps: (DocumentsRep | null)[],
    setReps: (reps: (DocumentsRep | null)[]) => void,
  ) => (
    <Stack spacing={2}>
      {[0, 1, 2].map((index) => (
        <Autocomplete
          key={index}
          size="small"
          options={salesPersonOptions}
          value={reps[index]}
          getOptionLabel={getRepLabel}
          onChange={(_, value) => setRepAt(reps, setReps, index, value)}
          renderInput={(params) => (
            <TextField {...params} size="small" label={`Sales Person ${index + 1}`} />
          )}
        />
      ))}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Update Order Sales Reps?</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This contact is a new Marketing Source and has sales reps tied to it.
          Choose which sales reps should be written to the order.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">
                Current order marketing-source sales reps
              </Typography>
              {renderRepForm(orderSalesReps, setOrderSalesReps)}
              <Button size="small"
                variant="contained"
                onClick={() => onSubmit(orderSalesReps)}
              >
                Use These
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">
                Sales reps tied to the selected contact
              </Typography>
              {renderRepForm(
                selectedContactSalesReps,
                setSelectedContactSalesReps,
              )}
              <Button size="small"
                variant="contained"
                onClick={() => onSubmit(selectedContactSalesReps)}
              >
                Use These
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

const logDocumentContactEvent = (
  mode: "add" | "edit",
  previousContact: DocumentsOrderContact | null | undefined,
  updatedContact: DocumentsOrderContact,
) => {
  logEvent(
    "Documents Page",
    mode === "edit" ? "EditContact" : "AddContact",
    JSON.stringify(mode === "edit" ? { before: previousContact, updated: updatedContact } : updatedContact),
  );
};

const getAxiosErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (!axios.isAxiosError(error)) {
    return fallbackMessage;
  }

  const responseData = error.response?.data as
    | { message?: string; errorMessage?: string }
    | string
    | undefined;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (responseData && typeof responseData === "object") {
    return (
      responseData.message ||
      responseData.errorMessage ||
      error.message ||
      fallbackMessage
    );
  }

  return error.message || fallbackMessage;
};
